import React, { useEffect, useState } from "react";
import { connect } from "react-redux";

import Actions from "../actions";
import { clickToLinkHandler } from "../helpers/utils";
import HeaderLogoIcon from "./common/icons/navigationHeader/headerLogoIcon";
import * as api from "../api/generated-api";

import { configureAuthHeader, configureBaseUrl } from "../api/axiosConfig";
import { getDefaultOrganization } from "../api/organizations";
import { Business, Logout, WorkOutline } from "@mui/icons-material";
import { AppBar, Box, SxProps, useTheme } from "@mui/material";
import { UserWithPersonWithOrganizationPersons } from "../api/types";

type MainNavigationProps = {
  user: UserWithPersonWithOrganizationPersons;
  token: string;
  location: { pathname: string };
  resetReduxStore: () => void;
  logout: () => void;
  isAllowedClients: boolean;
};

type HeaderState = {
  isDealsHoverIcon: boolean;
  isCompanyInfoHoverIcon: boolean;
  isClientsHoverIcon: boolean;
  isLogoutHoverIcon: boolean;
};

const MainNavigation = (props: MainNavigationProps) => {
  const { token, user, resetReduxStore, logout } = props;
  const [state, setState] = useState<HeaderState>({
    isDealsHoverIcon: false,
    isCompanyInfoHoverIcon: false,
    isClientsHoverIcon: false,
    isLogoutHoverIcon: false,
  });

  const isSuperAdmin = (
    user: UserWithPersonWithOrganizationPersons
  ): boolean => {
    return user.role_id == 1;
  };

  configureBaseUrl();
  configureAuthHeader(token);

  const theme = useTheme();
  const headerIconStyling: SxProps = {
    cursor: "pointer",
    marginLeft: "28px",
    marginRight: "28px",
    paddingBottom: "15px",
  };

  const [currentOrganization, setCurrentOrganization] =
    useState<api.Organization>();

  useEffect(() => {
    getDefaultOrganization((fetchedCurrentOrganization) => {
      setCurrentOrganization(fetchedCurrentOrganization);
    });
  }, [token]);

  return (
    <AppBar
      position={"sticky"}
      sx={{ right: "auto", left: 0 }}
      style={{
        width: "80px",
        backgroundColor: theme.palette.tertiary.darker,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        alignItems: "center",
        zIndex: 4,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
          alignItems: "center",
          position: "relative",
        }}
      >
        {!isSuperAdmin(user) && (
          <HeaderLogoIcon
            sx={{ cursor: "pointer" }}
            data-testid="header-nav-deals"
            onClick={clickToLinkHandler.bind(this, "/")}
          />
        )}
        {!isSuperAdmin(user) &&
          currentOrganization?.type == api.OrganizationType.Company && (
            <Business
              data-testid="header-company-info"
              style={{
                color: state.isCompanyInfoHoverIcon
                  ? theme.palette.primary.main
                  : theme.palette.tertiary.lighter,
              }}
              sx={headerIconStyling}
              onMouseEnter={() => {
                setState({
                  ...state,
                  isLogoutHoverIcon: false,
                  isCompanyInfoHoverIcon: true,
                  isClientsHoverIcon: false,
                  isDealsHoverIcon: false,
                });
              }}
              onMouseLeave={() => {
                setState({
                  ...state,
                  isClientsHoverIcon: false,
                });
              }}
              onClick={() => {
                clickToLinkHandler(`/companies/${currentOrganization.id}`);
              }}
            />
          )}
        {currentOrganization?.type == api.OrganizationType.Law_Firm && (
          <WorkOutline
            data-testid="header-clients"
            style={{
              color: state.isClientsHoverIcon
                ? theme.palette.primary.main
                : theme.palette.tertiary.lighter,
            }}
            sx={headerIconStyling}
            onMouseEnter={() => {
              setState({
                ...state,
                isLogoutHoverIcon: false,
                isCompanyInfoHoverIcon: false,
                isClientsHoverIcon: true,
                isDealsHoverIcon: false,
              });
            }}
            onMouseLeave={() => {
              setState({
                ...state,
                isClientsHoverIcon: false,
              });
            }}
            onClick={() => {
              clickToLinkHandler(
                `/law-firms/${currentOrganization.id}/clients`
              );
            }}
          />
        )}
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-end",
          alignItems: "center",
          height: 312,
          backgroundImage: 'url("/assets/header-gradient.svg")',
        }}
      >
        <Logout
          style={{
            color: state.isLogoutHoverIcon
              ? theme.palette.primary.main
              : theme.palette.tertiary.lighter,
          }}
          sx={{ ...headerIconStyling, marginBottom: "20px" }}
          data-testid="header-logout"
          fill={
            state.isLogoutHoverIcon
              ? theme.palette.primary.main
              : theme.palette.tertiary.lighter
          }
          onClick={() => {
            resetReduxStore();
            logout();
          }}
          onMouseEnter={() =>
            setState({
              ...state,
              isLogoutHoverIcon: true,
              isClientsHoverIcon: false,
              isDealsHoverIcon: false,
            })
          }
          onMouseLeave={() =>
            setState({
              ...state,
              isLogoutHoverIcon: false,
            })
          }
        />
      </Box>
    </AppBar>
  );
};

const mapStateToProps = (state) => {
  const { user, token } = state.auth;
  return {
    user,
    token,
  };
};

const mapDispatchToProps = (dispatch) => ({
  logout: function () {
    dispatch(Actions.logout());
  },
  resetReduxStore: function () {
    dispatch({ type: "RESET_STORE" });
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(MainNavigation);
